import { useEffect, useRef, useState } from "react";
import { Download, FileText, Trash2, Upload, Plus, Loader2 } from "lucide-react";
import api from "../../lib/api";

const TABS = [
  { key: "financial_reports", label: "Financial reports" },
  { key: "performance",       label: "Performance" },
  { key: "proof_of_payment",  label: "Proof of payment" },
];

/**
 * Admin "Reports" card — embedded inside AdminMerchantDetail.
 * Three sub-tabs matching the merchant view; per-tab list + upload form.
 */
export default function AdminMerchantReports({ merchantId }) {
  const [tab, setTab] = useState("financial_reports");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/merchants/${merchantId}/reports`, { params: { tab } });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => { try { await refresh(); } catch { if (!cancelled) setItems([]); } })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, tab]);

  const onUploaded = async () => { setShowForm(false); await refresh(); };
  const onDelete = async (report_id) => {
    if (!window.confirm("Soft-delete this report? The merchant will no longer see it; the file is preserved for audit.")) return;
    await api.delete(`/admin/merchants/${merchantId}/reports/${report_id}`);
    await refresh();
  };

  return (
    <div data-testid="admin-merchant-reports" className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Reports</div>
          <div className="text-[12px] text-zinc-400 mt-1">Files visible to the merchant on the Financials page.</div>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          data-testid="admin-reports-upload-toggle"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] text-white px-3 py-1.5 text-[12px]"
        >
          <Plus className="h-3 w-3" /> {showForm ? "Cancel" : "Upload report"}
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-white/[0.06]" data-testid="admin-reports-subtabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`admin-reports-subtab-${t.key}`}
            className={`relative px-3 py-2 text-[12.5px] transition-colors ${
              tab === t.key ? "text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            {t.label}
            {tab === t.key && (
              <span className="absolute left-0 right-0 bottom-[-1px] h-[2px] bg-gradient-to-r from-[#00E5FF] to-[#0055FF]" />
            )}
          </button>
        ))}
      </div>

      {showForm && (
        <UploadForm merchantId={merchantId} tab={tab} onUploaded={onUploaded} />
      )}

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] text-zinc-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/[0.08] bg-white/[0.01] p-6 text-center text-[12.5px] text-zinc-500">
            No reports in this tab yet.
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid={`admin-reports-list-${tab}`}>
            {items.map((r) => (
              <li
                key={r.report_id}
                className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.05] bg-white/[0.015] px-3 py-2.5 text-[12.5px]"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {r.kind === "crypto_link" ? (
                    <span className="text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-amber-400/30 bg-amber-400/[0.06] text-amber-200 shrink-0">CRYPTO</span>
                  ) : (
                    <FileText className="h-3.5 w-3.5 text-[#00E5FF] shrink-0" />
                  )}
                  <span className="text-white truncate" title={r.title}>{r.title}</span>
                  <span className="text-zinc-500 shrink-0">· {r.period}</span>
                  {r.payment_date && (
                    <span className="text-zinc-500 shrink-0">· {r.payment_date}</span>
                  )}
                  {r.payment_amount && (
                    <span className="text-zinc-300 shrink-0 font-mono">
                      {r.payment_amount}{r.payment_currency ? ` ${r.payment_currency}` : ""}
                    </span>
                  )}
                  {r.kind === "crypto_link" ? (
                    <a
                      href={r.crypto_url} target="_blank" rel="noopener noreferrer"
                      className="ml-auto text-[11px] text-cyan-300 hover:text-cyan-200 truncate max-w-[40%] font-mono shrink-0"
                      title={r.crypto_url}
                    >
                      {r.crypto_url}
                    </a>
                  ) : (
                    <span className="ref-mono text-zinc-600 ml-auto shrink-0">{(r.size / (1024 * 1024)).toFixed(2)} MB</span>
                  )}
                </div>
                <span className="text-[11px] text-zinc-500 shrink-0">{new Date(r.uploaded_at).toLocaleDateString()}</span>
                <button
                  type="button"
                  onClick={() => onDelete(r.report_id)}
                  data-testid={`admin-reports-delete-${r.report_id}`}
                  className="text-zinc-500 hover:text-rose-300"
                  aria-label="Soft-delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function UploadForm({ merchantId, tab, onUploaded }) {
  const [title, setTitle] = useState("");
  const [period, setPeriod] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Brief 3.13 (Feb 25 2026) — proof-of-payment supports two flavours:
  //   bank: file proof + payment date + amount/currency
  //   crypto: txn URL + payment date + amount/currency (no file)
  const isProof = tab === "proof_of_payment";
  const isPerformance = tab === "performance";
  const [kind, setKind] = useState("file");
  const [cryptoUrl, setCryptoUrl] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentCurrency, setPaymentCurrency] = useState("");
  const fileRef = useRef(null);

  // Reset variant state when the tab changes.
  /* eslint-disable */
  useEffect(() => {
    setKind("file");
    setCryptoUrl("");
    setPaymentDate("");
    setPaymentAmount("");
    setPaymentCurrency("");
    setErr("");
  }, [tab]);
  /* eslint-enable */

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !period.trim()) return;
    const usingCrypto = isProof && kind === "crypto_link";
    if (usingCrypto) {
      if (!cryptoUrl.trim() || !/^https?:\/\//i.test(cryptoUrl.trim())) {
        setErr("Paste a valid transaction URL (https://…).");
        return;
      }
    } else {
      const file = fileRef.current?.files?.[0];
      if (!file) {
        setErr("Choose a file to upload.");
        return;
      }
    }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("period", period.trim());
      fd.append("tab", tab);
      if (usingCrypto) {
        fd.append("kind", "crypto_link");
        fd.append("crypto_url", cryptoUrl.trim());
      } else {
        const file = fileRef.current?.files?.[0];
        fd.append("file", file);
        fd.append("kind", "file");
      }
      if (isProof) {
        if (paymentDate) fd.append("payment_date", paymentDate);
        if (paymentAmount) fd.append("payment_amount", paymentAmount);
        if (paymentCurrency) fd.append("payment_currency", paymentCurrency);
      }
      await api.post(`/admin/merchants/${merchantId}/reports/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTitle(""); setPeriod("");
      setCryptoUrl(""); setPaymentDate(""); setPaymentAmount(""); setPaymentCurrency("");
      if (fileRef.current) fileRef.current.value = "";
      await onUploaded();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  // Helpful period placeholder per tab.
  const periodPlaceholder = isPerformance
    ? "Week (e.g. W23 2026, 03–09 Jun 2026)"
    : isProof
    ? "Period (e.g. May 2026, Q1 2026)"
    : "Period (e.g. Q1 2026, January 2026)";
  const titlePlaceholder = isPerformance
    ? "Title (e.g. Weekly performance — W23 2026)"
    : isProof
    ? (kind === "crypto_link"
        ? "Title (e.g. USDT TRC20 payout · 12 May 2026)"
        : "Title (e.g. Wire payout · invoice 1042)")
    : "Title (e.g. Q1 2026 settlement summary)";

  return (
    <form onSubmit={submit} className="mt-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-3" data-testid="admin-reports-upload-form">
      {isProof && (
        <div className="flex items-center gap-2" data-testid="admin-reports-kind-toggle">
          <span className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Type</span>
          {[
            { key: "file", label: "Bank (file)" },
            { key: "crypto_link", label: "Crypto (URL)" },
          ].map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => setKind(k.key)}
              data-testid={`admin-reports-kind-${k.key}`}
              className={`rounded-full px-3 py-1 text-[11.5px] border transition-colors ${
                kind === k.key
                  ? "border-cyan-400/50 bg-cyan-400/[0.10] text-cyan-100"
                  : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:border-white/30"
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={titlePlaceholder} maxLength={200} required
          data-testid="admin-reports-title-input"
          className="rounded-lg bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600"
        />
        <input
          type="text" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder={periodPlaceholder} maxLength={100} required
          data-testid="admin-reports-period-input"
          className="rounded-lg bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600"
        />
      </div>

      {isProof && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-[11px] text-zinc-500">
            Payment date
            <input
              type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
              data-testid="admin-reports-payment-date"
              className="mt-1 w-full rounded-lg bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600"
            />
          </label>
          <label className="text-[11px] text-zinc-500">
            Amount (optional)
            <input
              type="text" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="e.g. 12,450.00"
              data-testid="admin-reports-payment-amount"
              className="mt-1 w-full rounded-lg bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600"
            />
          </label>
          <label className="text-[11px] text-zinc-500">
            Currency (optional)
            <input
              type="text" value={paymentCurrency} onChange={(e) => setPaymentCurrency(e.target.value.toUpperCase())}
              placeholder="EUR, USDT…"
              maxLength={8}
              data-testid="admin-reports-payment-currency"
              className="mt-1 w-full rounded-lg bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 font-mono"
            />
          </label>
        </div>
      )}

      {isProof && kind === "crypto_link" ? (
        <input
          type="url"
          value={cryptoUrl}
          onChange={(e) => setCryptoUrl(e.target.value)}
          placeholder="Transaction URL (e.g. https://tronscan.org/#/transaction/abc…)"
          data-testid="admin-reports-crypto-url"
          className="w-full rounded-lg bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 font-mono"
        />
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileRef}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
            data-testid="admin-reports-file-input"
            className="text-[12px] text-zinc-300 file:rounded-lg file:bg-white/[0.04] file:border file:border-white/[0.08] file:text-white file:px-3 file:py-1.5 file:text-[12px] file:mr-3"
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="text-[11px] text-zinc-500 flex-1">
          {isProof && kind === "crypto_link"
            ? "Crypto-link: no file is stored. The merchant sees the URL + payment metadata."
            : `PDF / DOC / XLS / CSV / JPG / PNG · max 25 MB · uploads to ${tab}`}
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-reports-upload-submit"
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-50 px-4 py-2 text-[12.5px] font-medium"
        >
          <Upload className="h-3 w-3" />
          {busy ? "Saving…" : (isProof && kind === "crypto_link" ? "Save link" : "Upload")}
        </button>
      </div>
      {err && <div className="text-[12px] text-rose-200" data-testid="admin-reports-upload-error">{err}</div>}
    </form>
  );
}
